import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../rootReducer';
import QuickEdgeStyling from './drawer-views/edge-styling/QuickEdgeStyling';
import EdgeSettings from './drawer-views/graph-settings/EdgeSettings';
import Layout from './drawer-views/layout/Layout';
import ZoomSettings from './drawer-views/layout/ZoomSettings';
import QuickNodeStyling from './drawer-views/node-styling/QuickNodeStyling';
import Projections from './drawer-views/projections/Projections';
import EdgeStyling from './drawer-views/edge-styling/EdgeStyling';
import GraphSettings from './drawer-views/graph-settings/GraphSettings';
import QuickLayout from './drawer-views/layout/QuickLayout';
import NodeStyling from './drawer-views/node-styling/NodeStyling';
import { nextTime, previousTime } from './drawer-views/data/dataSlice';
import { setLocalNecessity } from './drawer-views/edge-styling/edgeStylingSlice';
import Time from './drawer-views/time/Time';
import { AlloyInstance } from 'alloy-ts';
import LocalNecessity from './drawer-views/local-necessity/LocalNecessity';
import { SterlingConnection } from '../../sterling/SterlingConnection';

// Map redux state to graph settings props
const mapState = (state: RootState) => ({
    current: state.graphSlice.dataSlice.currentInstance,
    instances: state.graphSlice.dataSlice.instances,
    localNecessity: state.graphSlice.edgeStylingSlice.localNecessity,
    loop: state.graphSlice.dataSlice.loop,
    numInstances: state.sterlingSlice.instance.length,
    view: state.sterlingSlice.graphView
});

const mapDispatch = {
    nextTime,
    previousTime,
    setLocalNecessity
}

// Create connector
const connector = connect(mapState, mapDispatch);

// Create props for things from redux
type GraphDrawerProps = ConnectedProps<typeof connector> & {
    connection: SterlingConnection
};

// The graph settings component
const GraphDrawer: React.FunctionComponent<GraphDrawerProps> = props => {

    const {current, instances, loop, nextTime, previousTime} = props;

    if (props.view === 'node') return (
        <>
            {
                props.numInstances > 1 && <Time
                    current={current}
                    nextTime={nextTime}
                    previousTime={previousTime}
                    loop={loop}/>
            }
            <Projections/>
            <QuickNodeStyling/>
            <NodeStyling/>
        </>
    );
    if (props.view === 'edge') return (
        <>
            {
                props.numInstances > 1 && <Time
                    current={current}
                    nextTime={nextTime}
                    previousTime={previousTime}
                    loop={loop}/>
            }
            {
                hasLocalNecessity(instances[current]) && <LocalNecessity
                    localNecessity={props.localNecessity}
                    setLocalNecessity={(ln: string | null) => {
                        if (ln !== null) {
                            const msg = ln  === 'true' ? 'LN+' : 'LN-'
                            props.connection.sendMessage(`NOTIFY:GRAPH:${msg}`);
                        }
                        props.setLocalNecessity(ln)
                    }}
                />
            }
            <QuickEdgeStyling/>
            <EdgeStyling/>
        </>
    );
    if (props.view === 'layout') return (
        <>
            {
                props.numInstances > 1 && <Time
                    current={current}
                    nextTime={nextTime}
                    previousTime={previousTime}
                    loop={loop}/>
            }
            <QuickLayout/>
            <Layout/>
            <ZoomSettings/>
        </>
    );
    if (props.view === 'settings') return (
        <>
            {
                props.numInstances > 1 && <Time
                    current={current}
                    nextTime={nextTime}
                    previousTime={previousTime}
                    loop={loop}/>
            }
            <GraphSettings/>
            <EdgeSettings/>
        </>
    );

    return null;
};

function hasLocalNecessity (current: AlloyInstance | undefined) {
    if (current) {
        return current.fields().some(field => {
            return field.tuples().some(tuple => {
                return tuple.attributes().hasOwnProperty('LN');
            });
        });
    }
    return false;
}

export default connector(GraphDrawer);
